import React, { useState, useMemo } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
// ONB-1. THE FIELDS ARE NOT TYPED INTO THIS PAGE. They are rendered from the module the Cloud
// Function validates against, so the form and the server can never come to disagree about what is
// asked, what is required, or what is refused (directives section XI). A field added to
// src\data\applicationRecord.js appears here; one removed disappears. Nobody edits two files.
import { BLOCKS, CLAIMED_OFFICE, PAY_BASES, validateApplication, reasonFor } from '../data/applicationRecord';
import { NOTICE_VERSION, ACKNOWLEDGEMENTS, noticeText, tickValue } from '../data/applicationNotice';
import { appCheckSiteKeyPresent, startAppCheck, appCheckReady } from '../appCheck';
// SECTION 341 (2026-09-22). Craft, railroad and work location are CHOSEN, from the same lists New Claim
// uses, and the Local and the General Committee are DERIVED from them - Bob's doctrine of record,
// data/LC_REPRESENTATION_DOCTRINE.md section 5: "LC resolves on three keys: craft x railroad x territory",
// and 5.2, the local number does not identify an LC. Before this the form asked him to type all five.
import { RAILROADS, LOCATIONS_BY_RAILROAD } from '../data/agreementData';
import { DIVISIONS, craftsInDivision } from '../data/craftTerms';   // section 807
import DivisionButtons from '../components/DivisionButtons';
import { resolveApplicantRouting } from '../data/routing';
// Section 342: Bob's approved words on the process and the roles - one source, also the handbook's section 0.
import { GUIDE_TITLE, PROCESS_TITLE, PROCESS, ROLES_TITLE, ROLES } from '../data/applicantGuide';
// Section 352: requirements v4 lets him attach a copy of the seniority roster. The limits are the
// module's, and the Cloud Function checks them again on arrival against the bytes it actually decoded.
import { ALLOWED_ACCEPT, MAX_BYTES, validateExhibit } from '../data/applicationExhibit';
// Record 612 (Bob 10/8, item (d)): the claims he may record with his application, checked by the module the server
// checks them with; the kinds are the claim categories the intake screens offer.
import { validatePendingClaims, MAX_PENDING_CLAIMS } from '../data/applicationDesk';
import { CLAIM_KINDS } from '../data/box1Guided';
import { PLATFORM_NAME } from '../installation';   // section 768: the installation's own name (ruling 312)

const locationsFor = (craft, railroad) =>
  LOCATIONS_BY_RAILROAD[(craft ? craft + '\u241F' : '') + railroad] || LOCATIONS_BY_RAILROAD[railroad] || [];

// The label a man reads, for a field name a programmer reads. Kept beside the blocks it labels and
// nowhere else; a field with no label here falls back to its own name rather than vanishing, because
// a form that silently drops a REQUIRED field cannot be submitted and will not say why.
const LABELS = {
  fullLegalName: 'Full legal name (as the carrier holds it)',
  mailingAddress: 'Mailing address',
  email: 'Personal e-mail address',
  mobile: 'Mobile number',
  rosterNumber: 'Roster number',
  seniorityDate: 'Seniority date',
  railroad: 'Railroad',
  craftAndClass: 'Craft and class',
  workLocation: 'Work location / home terminal',
  employeeNumber: 'Employee number',
  jobSymbol: 'Job symbol / assignment',
  payRate: 'Your rate of pay',
  payBasis: 'Per hour, per day or per week',
  unionName: 'Union',
  generalCommittee: 'General Committee',
  localCommittee: 'Local',
  claimedOffice: 'The office you hold, if any',
};

const BLOCK_TITLES = {
  personal: 'Personal contact',
  work: 'Work',
  union: 'Union',
};

const PAY_BASIS_LABELS = { hourly: 'Per hour', daily: 'Per day', weekly: 'Per week' };   // section 774

const OFFICE_LABELS = {
  none: 'None - I hold no office',
  local_chair: 'Local Chairperson',
  general_chair: 'General Chairperson',
  other: 'Another office',
};

function Guide() {
  return (
    <div style={{ background: '#0A1A2A', border: '1px solid #2B5C8A', borderRadius: 6, padding: '12px 16px', marginBottom: 18, fontSize: 13, lineHeight: 1.6 }}>
      <h3 style={{ fontSize: 13, letterSpacing: 1, textTransform: 'uppercase', color: '#8AA4BF', margin: '0 0 8px' }}>{GUIDE_TITLE}</h3>
      <strong>{PROCESS_TITLE}</strong>
      <ol style={{ margin: '6px 0 10px', paddingLeft: 20 }}>
        {PROCESS.map(([head, body]) => <li key={head} style={{ marginBottom: 4 }}><strong>{head}</strong> {body}</li>)}
      </ol>
      <strong>{ROLES_TITLE}</strong>
      <ul style={{ margin: '6px 0 0', paddingLeft: 20 }}>
        {ROLES.map(([head, body]) => <li key={head} style={{ marginBottom: 4 }}><strong>{head}</strong> {body}</li>)}
      </ul>
    </div>
  );
}

export default function Apply({ onBack }) {
  const [form, setForm] = useState({});
  const [division, setDivision] = useState('');   // section 807: the division button
  const [ticked, setTicked] = useState({});
  const [error, setError] = useState('');
  const [sending, setSending] = useState(false);
  const [reference, setReference] = useState('');

  // Start App Check on first render if a site key was built in. If there is none, the page says so
  // up front - see src\appCheck.js. It never submits in hope.
  useMemo(() => { startAppCheck(); return null; }, []);
  const gateOpen = appCheckSiteKeyPresent() && appCheckReady();

  const set = (k, v) => setForm((f) => {
    const next = { ...f, [k]: v };
    // A new craft empties the railroad and location beneath it; a new railroad empties the location.
    if (k === 'craftAndClass') { next.railroad = ''; next.workLocation = ''; }
    if (k === 'railroad') next.workLocation = '';
    return next;
  });
  const [notListed, setNotListed] = useState(false);
  const [exhibit, setExhibit] = useState(null);        // the roster copy, optional (requirements v4)
  const [exhibitError, setExhibitError] = useState('');
  const [pendingClaims, setPendingClaims] = useState([]);   // Record 612 (item (d)): held with the application
  const setPc = (i, k, v) => setPendingClaims((l) => l.map((c, j) => (j === i ? { ...c, [k]: v } : c)));
  // The route he is told, from his three answers - the same function the server runs on arrival.
  const route = resolveApplicantRouting(form.craftAndClass, form.railroad, form.workLocation);
  const tick = (k) => setTicked((t) => ({ ...t, [k]: t[k] ? '' : tickValue() }));

  const untickedBoxes = ACKNOWLEDGEMENTS.filter((a) => !ticked[a.key]);

  const takeExhibit = (file) => {
    setExhibitError('');
    if (!file) { setExhibit(null); return; }
    const v = validateExhibit({ contentType: file.type, bytes: file.size });
    if (!v.ok) { setExhibit(null); setExhibitError(v.why); return; }
    const reader = new FileReader();
    reader.onload = () => {
      const s = String(reader.result || '');
      const comma = s.indexOf(',');
      setExhibit({ name: file.name, contentType: file.type, base64: comma >= 0 ? s.slice(comma + 1) : '' });
    };
    reader.onerror = () => setExhibitError('That file could not be read from your device.');
    reader.readAsDataURL(file);
  };

  const submit = async () => {
    setError('');
    // The SAME validation the callable runs, run here first - so he is told at the field, not after
    // a round trip. It is not a substitute for the server's; the server runs it again on arrival.
    const v = validateApplication(form);
    if (!v.ok) {
      setError(v.missing.length ? 'Please complete: ' + v.missing.map((k) => LABELS[k] || k).join(', ') : (v.badPay || 'Please check the form.'));
      return;
    }
    if (untickedBoxes.length) {
      setError('Please read and tick: ' + untickedBoxes.map((a) => a.text).join('  |  '));
      return;
    }
    // Record 612 (item (d)): the claims he recorded, checked here first by the module the server checks them with.
    const pc = validatePendingClaims(pendingClaims);
    if (!pc.ok) { setError(pc.why); return; }
    setSending(true);
    try {
      const fn = httpsCallable(getFunctions(), 'submitApplication');
      const res = await fn({
        // Sent for the record only; the server derives both again and uses its own answer.
        form: { ...form, generalCommittee: route.generalCommittee, localCommittee: route.localCommittee },
        // The tick records WHICH WORDS he agreed to, not a bare true (ONB-5).
        acknowledgements: Object.fromEntries(ACKNOWLEDGEMENTS.map((a) => [a.key, ticked[a.key]])),
        accuracyNoticeVersion: NOTICE_VERSION,
        exhibit,        // null unless he attached one - the server treats absence as absence, not a fault
        pendingClaims: pc.clean,   // Record 612 (item (d)): held with the application until it is decided
      });
      setReference((res && res.data && res.data.reference) || '');
    } catch (e) {
      const code = (e && e.code) || '';
      // Say what actually happened, as Login does. 'already-exists' is not an error he made.
      if (/already-exists/.test(code)) {
        setError('An application is already open for you. Your Local Chairperson has it.');
      } else if (/failed-precondition/.test(code)) {
        setError((e && e.message) || 'This application could not be accepted from here.');
      } else {
        setError((e && e.message) || 'The application could not be sent. Check your connection and try again.');
      }
    } finally {
      setSending(false);
    }
  };

  const hint = (t) => <div style={{ fontSize: 12, color: '#6F8AA6', padding: '8px 0' }}>{t}</div>;
  const linkBtn = (label, onClick) => (
    <button type="button" onClick={onClick}
      style={{ background: 'none', border: 'none', color: '#8AA4BF', fontSize: 11, cursor: 'pointer', textDecoration: 'underline', padding: '4px 0' }}>
      {label}
    </button>
  );
  const sel = (field, options, placeholder) => (
    <select aria-label={LABELS[field] || field} id={'apply-' + field} className="form-input" value={form[field] || ''} onChange={(e) => set(field, e.target.value)}>
      <option value="">{placeholder}</option>
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  );

  const renderField = (field) => {
    if (field === 'claimedOffice') {
      return (
        <select aria-label={LABELS[field] || field} id={'apply-' + field} className="form-input" value={form[field] || 'none'} onChange={(e) => set(field, e.target.value)}>
          {CLAIMED_OFFICE.map((o) => <option key={o} value={o}>{OFFICE_LABELS[o] || o}</option>)}
        </select>
      );
    }
    // Section 807 (Bob 2026-10-26): the division is picked by a button; the craft list shows only its crafts.
    if (field === 'craftAndClass') return (
      <>
        <DivisionButtons value={division} onPick={(d) => { setDivision(d); set('craftAndClass', ''); }} />
        {DIVISIONS.length > 1 && !division ? hint('Choose Yardmaster or Train & Engine first.') : sel(field, craftsInDivision(division, Object.keys(RAILROADS)), 'Choose your craft')}
      </>
    );
    // Section 774 made pay optional; SUPERSEDED 2026-10-25 - every applicant enters his rate of pay at signup.
    if (field === 'payRate') {
      return <input aria-label="For example 38.50" id={'apply-' + field} className="form-input" type="text" inputMode="decimal" placeholder="For example 38.50"
        value={form[field] || ''} onChange={(e) => set(field, e.target.value)} />;
    }
    if (field === 'payBasis') {
      return (
        <select aria-label={LABELS[field] || field} id={'apply-' + field} className="form-input" value={form[field] || ''} onChange={(e) => set(field, e.target.value)}>
          <option value="">Choose per hour, per day or per week</option>
          {PAY_BASES.map((b) => <option key={b} value={b}>{PAY_BASIS_LABELS[b]}</option>)}
        </select>
      );
    }
    if (field === 'railroad') {
      return form.craftAndClass ? sel(field, RAILROADS[form.craftAndClass] || [], 'Choose your railroad') : hint('Choose your craft first.');
    }
    if (field === 'workLocation') {
      if (!form.railroad) return hint('Choose your railroad first.');
      const list = locationsFor(form.craftAndClass, form.railroad);
      if (notListed || !list.length) {
        return (
          <div>
            <input aria-label="Your work location / home terminal, as the carrier names it" id={'apply-' + field} className="form-input" type="text" value={form[field] || ''}
              placeholder="Your work location / home terminal, as the carrier names it"
              onChange={(e) => set(field, e.target.value)} />
            {list.length > 0 && linkBtn('Choose from the list instead', () => { setNotListed(false); set(field, ''); })}
          </div>
        );
      }
      return (
        <div>
          {sel(field, list, 'Choose your work location')}
          {linkBtn('My location is not listed', () => { setNotListed(true); set(field, ''); })}
        </div>
      );
    }
    if (field === 'generalCommittee' || field === 'localCommittee') {
      // Shown, never typed (doctrine 5.2).
      const v = route[field];
      const shown = v
        ? (field === 'localCommittee' ? 'Local ' + v : v)
        : (field === 'localCommittee' && route.generalCommittee
            ? 'Assigned by your General Chairperson'
            : 'Filled in from your craft, railroad and work location');
      return <div id={'apply-' + field} className="form-input" style={{ opacity: 0.85 }} aria-readonly="true">{shown}</div>;
    }
    return (
      <input aria-label={LABELS[field] || field} id={'apply-' + field} className="form-input"
        type={field === 'email' ? 'email' : field === 'seniorityDate' ? 'date' : 'text'}
        value={form[field] || ''} onChange={(e) => set(field, e.target.value)} />
    );
  };

  if (reference) {
    return (
      <div className="login-page">
        <div className="login-box" style={{ maxWidth: 620 }}>
          <div className="login-logo">
            <h1 style={{ margin: 0 }}>Application received</h1>
          </div>
          <p style={{ fontSize: 14, lineHeight: 1.6 }}>
            Your reference is <strong>{reference}</strong>. Write it down.
          </p>
          {/* Ruling 193.6: applying is not joining. He is told so plainly, here, before he waits. */}
          <p style={{ fontSize: 13, lineHeight: 1.6, color: '#8AA4BF' }}>
            This is an application, not an account. You cannot sign in until it is approved. Keep your
            reference: it is how your Local Chairperson or General Chairperson finds your application
            when you ask about it.
          </p>
          {/* Section 342: the process and the roles, the same words he read before submitting. The old
              promise of a mailed decision is gone: the platform sends no e-mail yet
              (section 341), and this page does not promise what the platform cannot do. */}
          <Guide />
          <hr className="login-divider" />
          <button className="btn btn-primary" style={{ width: '100%', justifyContent: 'center', padding: 12 }} onClick={onBack}>
            Back to sign in
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="login-page" style={{ alignItems: 'flex-start', overflowY: 'auto', paddingTop: 24, paddingBottom: 24 }}>
      <div className="login-box" style={{ maxWidth: 720 }}>
        <div className="login-logo">
          <h2>Apply to {PLATFORM_NAME}</h2>
          {/* RULING 274 (Bob, 2026-10-03): his own statement opens the Apply page. Text is his, word for word. */}
          <p className="pitch-line" style={{ fontStyle: 'italic', fontSize: 13, lineHeight: 1.6, color: '#C9D6E3', marginTop: 10 }}>
            {"The corporate dimension prevents workers from making and implementing common sense procedures and protocols. The handful of lives that are adversely affected are not as important as the stockholders and the bottom line."}
          </p>
          <p>An application is not an account. It goes to your Local Chairperson to decide.</p>
        </div>

        <Guide />

        {!gateOpen && (
          // Told BEFORE he types his work history, not after. See src\appCheck.js.
          <div className="alert-banner" style={{ marginBottom: 14 }}>
            &#9888; This site is not yet set up to accept applications. The administrator has to
            register it before the form will send. Nothing you type here can be submitted yet -
            please contact your Local Chairperson directly.
          </div>
        )}

        {error && <div className="alert-banner">&#9888; {error}</div>}

        {Object.keys(BLOCKS).map((blockKey) => (
          <div key={blockKey} style={{ marginBottom: 22 }}>
            <h3 style={{ fontSize: 13, letterSpacing: 1, textTransform: 'uppercase', color: '#8AA4BF', marginBottom: 10 }}>
              {BLOCK_TITLES[blockKey] || blockKey}
            </h3>
            {BLOCKS[blockKey].map(([field]) => (
              <div className="form-group" key={field}>
                <label className="form-label" htmlFor={'apply-' + field}>{LABELS[field] || field}</label>
                {renderField(field)}
                {/* RULING 193.5: every field says what it makes work, in the words the module holds.
                    A field whose reason nobody can state is a field nobody should be asked for. */}
                <div style={{ fontSize: 11, color: '#6F8AA6', marginTop: 4 }}>{reasonFor(field)}</div>
              </div>
            ))}
          </div>
        ))}

        {/* SECTION 352 (requirements v4): he may attach a copy of the seniority roster. OPTIONAL - ruling
            194 makes the cross-reference the platform's job, not his, and an application without an exhibit
            is not an exception. */}
        <div className="form-group">
          <label className="form-label" htmlFor="apply-exhibit">Seniority roster (optional)</label>
          <input id="apply-exhibit" type="file" accept={ALLOWED_ACCEPT}
            onChange={(e) => takeExhibit(e.target.files && e.target.files[0])}
            style={{ color: '#F4F6F8' }} />
          <div style={{ fontSize: 11, color: '#6F8AA6', marginTop: 4 }}>
            If you have a copy of your craft&#39;s seniority roster showing your number and date, attach it -
            PDF, JPG or PNG, up to {Math.round(MAX_BYTES / (1024 * 1024))} MB. It is read only by your Local
            Chairperson and your General Chairperson. You do not need it to apply.
          </div>
          {exhibit && <div style={{ fontSize: 11, color: '#40C070', marginTop: 4 }}>&#10003; {exhibit.name} attached</div>}
          {exhibitError && <div style={{ fontSize: 11, color: '#E06A60', marginTop: 4 }}>{exhibitError}</div>}
        </div>

        {/* Record 612 (Bob 10/8, item (d)): CLAIMS HELD UNTIL APPROVAL. He has no account until he is approved (ruling
            193.6), so he cannot file; he records the facts here, they wait on his application, his Local Chairperson
            sees them, and on approval each becomes a draft claim in his account. The time limit is said out loud. */}
        <div style={{ marginBottom: 22 }}>
          <h3 style={{ fontSize: 13, letterSpacing: 1, textTransform: 'uppercase', color: '#8AA4BF', marginBottom: 10 }}>
            Claims you want on record now (optional)
          </h3>
          <div style={{ fontSize: 12, color: '#8AA4BF', lineHeight: 1.6, marginBottom: 10 }}>
            If something has already happened that you want to claim, record the facts here - up to {MAX_PENDING_CLAIMS}.
            They are held with your application: your Local Chairperson sees them, and nothing is filed. If you are
            approved, each becomes a draft claim in your account for you to finish and file.{' '}
            <strong>Recording a claim here does not stop the time limit for filing it.</strong> If a deadline is close,
            contact your Local Chairperson now.
          </div>
          {pendingClaims.map((c, i) => (
            <div key={i} style={{ border: '1px solid #2B5C8A', borderRadius: 6, padding: '10px 12px', marginBottom: 10 }}>
              <div className="form-group">
                <label className="form-label" htmlFor={'apply-pc-date-' + i}>Date it happened</label>
                <input aria-label="Date it happened" id={'apply-pc-date-' + i} className="form-input" type="date" value={c.date || ''}
                  onChange={(e) => setPc(i, 'date', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor={'apply-pc-location-' + i}>Where it happened</label>
                <input aria-label="Where it happened" id={'apply-pc-location-' + i} className="form-input" type="text" value={c.location || ''}
                  onChange={(e) => setPc(i, 'location', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor={'apply-pc-what-' + i}>What happened</label>
                <textarea aria-label="What happened" id={'apply-pc-what-' + i} className="form-textarea" rows={3} value={c.whatHappened || ''}
                  onChange={(e) => setPc(i, 'whatHappened', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor={'apply-pc-kind-' + i}>Kind of claim (if you know it)</label>
                <select aria-label="Kind of claim (if you know it)" id={'apply-pc-kind-' + i} className="form-input" value={c.kind || ''}
                  onChange={(e) => setPc(i, 'kind', e.target.value)}>
                  <option value="">Not sure</option>
                  {CLAIM_KINDS.map((k) => <option key={k.id} value={k.id}>{k.label}</option>)}
                </select>
              </div>
              <button type="button" onClick={() => setPendingClaims((l) => l.filter((x, j) => j !== i))}
                style={{ background: 'none', border: 'none', color: '#8AA4BF', fontSize: 11, cursor: 'pointer', textDecoration: 'underline', padding: 0 }}>
                Remove this claim
              </button>
            </div>
          ))}
          {pendingClaims.length < MAX_PENDING_CLAIMS && (
            <button type="button" className="btn btn-secondary" style={{ fontSize: 12 }}
              onClick={() => setPendingClaims((l) => l.concat([{ date: '', location: '', whatHappened: '', kind: '' }]))}>
              + Record a claim
            </button>
          )}
        </div>

        {/* SECTION 341: where this goes, told to him BEFORE he submits - the indicators his Local
            Chairperson will see: craft, railroad, work location, Local, General Committee. */}
        {form.craftAndClass && form.railroad && form.workLocation && (
          <div style={{ background: '#0A1A12', border: '1px solid ' + (route.routingStatus === 'routed' ? '#2E7D4F' : '#8A7A20'), borderRadius: 6, padding: '10px 14px', marginBottom: 18, fontSize: 12.5, lineHeight: 1.6 }}>
            <strong>Where this application goes.</strong>{' '}
            {form.craftAndClass}, {form.railroad}, {form.workLocation}:{' '}
            {route.routingStatus === 'routed'
              ? <span>Local {route.localCommittee}, General Committee {route.generalCommittee}. Your Local Chairperson checks it against the seniority roster and decides it.</span>
              : <span>General Committee {route.generalCommittee || '(not yet known)'}. This location is not yet mapped to a Local, so your General Chairperson assigns it. It is not refused and it is not delayed for that.</span>}
          </div>
        )}

        {/* Ruling 193.2, said out loud where he answers it. */}
        {form.claimedOffice && form.claimedOffice !== 'none' && (
          <div style={{ background: '#1A1A0A', border: '1px solid #8A7A20', borderRadius: 6, padding: '10px 14px', marginBottom: 18, fontSize: 12, color: '#C9B85A' }}>
            Naming an office here is a statement by you. It grants nothing. If your application is
            approved you become a member; the office is recorded as claimed and confirmed separately,
            by the officer above the seat.
          </div>
        )}

        <h3 style={{ fontSize: 13, letterSpacing: 1, textTransform: 'uppercase', color: '#8AA4BF', marginBottom: 10 }}>
          Before you submit
        </h3>
        <div style={{ background: '#0A1A2A', border: '1px solid #2B5C8A', borderRadius: 6, padding: '12px 14px', marginBottom: 14, fontSize: 13, lineHeight: 1.6 }}>
          {noticeText(NOTICE_VERSION)}
        </div>

        {ACKNOWLEDGEMENTS.map((a) => (
          <label key={a.key} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginBottom: 12, fontSize: 13, lineHeight: 1.5, cursor: 'pointer' }}>
            <input aria-label="Attach your document"
              type="checkbox"
              checked={!!ticked[a.key]}
              onChange={() => tick(a.key)}
              style={{ marginTop: 3 }}
            />
            <span>{a.text}</span>
          </label>
        ))}

        <button
          className="btn btn-primary"
          style={{ width: '100%', justifyContent: 'center', padding: 12, marginTop: 10, opacity: sending || !gateOpen ? 0.6 : 1 }}
          onClick={submit}
          disabled={sending || !gateOpen}
        >
          {sending ? 'Sending...' : 'Submit application'}
        </button>

        <div style={{ textAlign: 'center', marginTop: 12 }}>
          <button onClick={onBack} style={{ background: 'none', border: 'none', color: '#8AA4BF', fontSize: 12, cursor: 'pointer', textDecoration: 'underline' }}>
            Back to sign in
          </button>
        </div>
        <hr className="login-divider" />
        <div className="login-footer">
          This form does not ask for your Social Security number, date of birth, bank details, dues
          status, discipline history or medical information. None of it is needed to file or carry a
          claim, so none of it is collected.
        </div>
      </div>
    </div>
  );
}
